/**
 * Lightweight logging utilities for error service
 * Focuses on privacy protection and analytics compatibility
 */

/**
 * Sanitize URL by removing query parameters and fragments
 * @param {string} url - Full URL that may contain sensitive data
 * @returns {Object} Sanitized URL parts { host, path }
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') {
    return { host: 'unknown', path: '/' };
  }

  try {
    const urlObj = new URL(url);
    return {
      host: urlObj.host || 'unknown',
      path: urlObj.pathname || '/'
    };
  } catch {
    // Fallback for invalid URLs
    try {
      // Try to extract basic path from relative URLs
      const pathMatch = url.match(/^([^?#]*)/);
      return {
        host: 'unknown',
        path: pathMatch ? pathMatch[1] : '/'
      };
    } catch {
      return { host: 'unknown', path: '/' };
    }
  }
}

/**
 * Truncate field to specified length for analytics limits
 * @param {string} value - Value to truncate
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Truncated value
 */
export function truncateField(value, maxLength = 100) {
  if (!value || typeof value !== 'string') return '';
  if (value.length <= maxLength) return value;
  return value.substring(0, maxLength - 3) + '...';
}

/**
 * Compute simple fingerprint for error deduplication
 * Uses a basic hash function to avoid external dependencies
 * @param {Object} errorInfo - Error information object
 * @returns {string} Fingerprint hash
 */
export function computeFingerprint({ name, message, category, component }) {
  // Normalize message by removing numbers and specific values
  const normalizedMessage = (message || '')
    .replace(/\b\d+\b/g, 'N') // Replace numbers with N
    .replace(/["'][^"']*["']/g, 'X') // Replace quoted strings with X
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  // Create a simple string to hash
  const fingerprintSource = [
    name || 'Error',
    category || 'unknown',
    component || 'unknown',
    normalizedMessage
  ].join('|');

  // Simple hash function (djb2 algorithm)
  let hash = 5381;
  for (let i = 0; i < fingerprintSource.length; i++) {
    const char = fingerprintSource.charCodeAt(i);
    hash = ((hash << 5) + hash) + char; // hash * 33 + char
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to hex string
  return Math.abs(hash).toString(16);
}

/**
 * Redact potentially sensitive fields from metadata
 * @param {Object} metadata - Metadata object that may contain PII
 * @returns {Object} Redacted metadata
 */
export function redactMetadata(metadata = {}) {
  if (!metadata || typeof metadata !== 'object') return {};

  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'auth',
    'email', 'phone', 'ssn', 'creditcard', 'cc'
  ];

  const redacted = {};
  
  for (const [key, value] of Object.entries(metadata)) {
    const lowerKey = key.toLowerCase();
    
    // Check if key contains sensitive patterns
    const isSensitive = sensitiveKeys.some(pattern => 
      lowerKey.includes(pattern)
    );

    if (isSensitive) {
      redacted[key] = '[REDACTED]';
    } else if (typeof value === 'string') {
      // Truncate long strings
      redacted[key] = truncateField(value, 100);
    } else if (typeof value === 'object' && value !== null) {
      // Recursively redact nested objects (one level deep for simplicity)
      redacted[key] = '[OBJECT]';
    } else {
      redacted[key] = value;
    }
  }

  return redacted;
}

/**
 * Prepare error data for analytics with all necessary truncation
 * @param {Object} errorInfo - Error information to prepare
 * @returns {Object} Analytics-safe parameters
 */
export function prepareAnalyticsParams(errorInfo) {
  const params = {
    error_category: truncateField(errorInfo.category, 40),
    error_component: truncateField(errorInfo.component, 40),
    error_action: truncateField(errorInfo.action, 40),
    error_message: truncateField(errorInfo.message, 100),
    error_name: truncateField(errorInfo.name, 40),
    error_severity: truncateField(errorInfo.severity, 40),
    error_timestamp: errorInfo.timestamp
  };

  // Add optional fields if present
  if (errorInfo.fingerprint) {
    params.error_fingerprint = truncateField(errorInfo.fingerprint, 40);
  }

  if (errorInfo.metadata?.errorCode) {
    params.error_code = truncateField(String(errorInfo.metadata.errorCode), 40);
  }

  if (errorInfo.metadata?.url_path) {
    params.error_url_path = truncateField(errorInfo.metadata.url_path, 100);
  }

  // Ensure we don't exceed Firebase's 25 parameter limit
  const entries = Object.entries(params);
  if (entries.length > 25) {
    // Keep most important params, drop the rest
    const priorityParams = [
      'error_category', 'error_component', 'error_message',
      'error_severity', 'error_fingerprint', 'error_timestamp'
    ];
    
    const kept = {};
    let count = 0;
    
    // First add priority params
    for (const key of priorityParams) {
      if (params[key] !== undefined && count < 25) {
        kept[key] = params[key];
        count++;
      }
    }
    
    // Then add remaining params up to limit
    for (const [key, value] of entries) {
      if (!kept[key] && count < 25) {
        kept[key] = value;
        count++;
      }
    }
    
    return kept;
  }

  return params;
}